import { loaders, BaseTexture, Texture } from 'pixi.js';
import IResourceDriver, { ResourceID, AtlasKey, IQueueItem, FramesHash } from './IResourceDriver';
import HowlerMiddleware from './HowlerPixiLoaderMiddleware';
import { isWebPSupported, shouldLoadAsWebP } from '../WebPFunctions';
import { Atlas } from './ResourceManager';
import { INTERMEDIATE_ASSETS_SCALE } from './intermediateAssets';
import { getAtlasByUrl } from './contexts';
import { report } from 'report';
import CDNMiddleware from './CDNMiddleware';

const atlasExtensions = ['.png', '.jpg', '.png.webp'];

export default class PIXIResource implements IResourceDriver {

    private readonly supportsWebP: boolean = false;
    private readonly loader: loaders.Loader;
    private atlasResources: PIXI.loaders.Resource[] = [];

    private readonly onProgress: (progress: number, resource?: PIXI.loaders.Resource) => void;
    private readonly onResourceLoaded: (
        resourceIDOrAtlasKey: ResourceID | AtlasKey,
        loadedData: unknown,
    ) => void;
    private readonly onResourceFailed: (resourceID: ResourceID, error: unknown) => void;

    constructor(
        onProgress: (progress: number) => void,
        onResourceLoaded: (
            resourceIDOrAtlasKey: ResourceID | AtlasKey,
            loadedData: unknown,
        ) => void,
        onResourceFailed: (resourceID: ResourceID, error: unknown) => void,
    ) {
        this.onProgress = onProgress;
        this.onResourceLoaded = onResourceLoaded;
        this.onResourceFailed = onResourceFailed;
        this.supportsWebP = isWebPSupported();
        this.loader = new loaders.Loader();

        if (this.supportsWebP) {
            this.loader.pre((
                resource: PIXI.loaders.Resource,
                next: any
            ) => {
                if (
                    shouldLoadAsWebP(resource)
                ) {
                    // @ts-ignore
                    resource.extension = "webp";
                    // @ts-ignore
                    resource.url = resource.url + ".webp";
                }
                next();
            });
        }

        this.loader.pre(CDNMiddleware);

        this.loader.use(HowlerMiddleware);
    }

    /**
     * Start to loading.
     */
    public async load(items: Array<IQueueItem>) {
        return new Promise<void>((resolve, reject) => {
            this.loader.once('complete', () => {
                this.loader.off('error');
                this.loader.off('progress');
                this.loader.off('load');
                resolve();
            });

            this.loader.on('error', (error, _, resource) => {
                console.error(error);

                // Clear texture caches.
                // FIXME: what if the name comes from other asset type (e.g. audio)
                //        and by accident matches texture's name?
                // TODO: what about atlas keys?
                // TODO: what about resources that have different names but share same url?
                BaseTexture.removeFromCache(resource.name);
                BaseTexture.removeFromCache(resource.url);
                Texture.removeFromCache(resource.name);
                Texture.removeFromCache(resource.url);

                // Inform that this resource failed to load.
                this.onResourceFailed.call(null, resource.name, error);
            });

            this.loader.on('progress', (loader: loaders.Loader, resource: PIXI.loaders.Resource) => {
                this.onProgress.call(null, loader.progress, resource);
            });

            this.loader.on('load', this.onLoaded, this);

            for (const item of items) {
                if (this.loader.resources[item.resourceID]) continue;

                try {
                    this.loader.add(item.resourceID, item.path, { crossOrigin: 'anonymous' });
                } catch (e) {
                    this.onResourceFailed.call(null, item.resourceID, e);
                }
            }

            // Start loading queued resources.
            this.loader.load();
        });
    }

    private onLoaded(_: loaders.Loader, resource: loaders.Resource): void {
        // @ts-ignore workaround for cdn
        const url = resource.originalUrl || resource.url;
        if (url) {
            const ext = atlasExtensions.find((ext) => url.endsWith(ext));
            const atlas = ext ? getAtlasByUrl(`${url.slice(0, -ext.length)}.json`) : null;

            if (atlas) {
                this.parseAtlas(resource, atlas);
                this.atlasResources.push(resource);
            }
        }

        this.handleResourceLoaded(resource);
    }

    public async loadFullQualityAtlases(): Promise<FramesHash> {
        const framesHash: Record<string, true> = {};
        const resources = this.atlasResources;
        const scaleSuffix = `@${INTERMEDIATE_ASSETS_SCALE}x`;
        const items: { name: string; imageUrl: string; atlas: Atlas }[] = [];
        this.atlasResources = [];

        for (const resource of resources) {
            if (!resource.url.includes(scaleSuffix)) continue;

            const imageUrl = resource.url.replace(scaleSuffix, '');
            const ext = atlasExtensions.find((ext) => resource.url.endsWith(ext));
            const atlas = getAtlasByUrl(`${imageUrl.slice(0, -ext.length)}.json`);

            if (atlas) {
                items.push({ name: resource.name, imageUrl, atlas });
            }
        }

        if (items.length === 0) {
            return framesHash;
        }

        const loader = new PIXI.loaders.Loader();

        for (const item of items) {
            Object.keys(item.atlas.frames).forEach((n) => {
                framesHash[n] = true;
            });
            loader.add(item.name, item.imageUrl);
        }

        loader.on('load', (loader, resource) => {
            const item = items.find((it) => it.name === resource.name);
            const atlasName = resource.name.slice(0, -'_image'.length);
            this.onResourceLoaded(atlasName, item.atlas);
            try {
                this.parseAtlas(resource, item.atlas);
            } catch (err) {
                if (
                    err
                ) {
                    report(`Failed to parse atlas`, {
                        data: {
                            atlasName: atlasName
                        },
                        error: err,
                        once: true,
                        sampleRate:
                            (err.message.indexOf("frame does not fit inside the base Texture dimensions") !== -1) ? 1 : 0.1,
                    });
                }

                return;
            }
            this.handleResourceLoaded(resource);
        });

        await new Promise((resolve) => loader.load(resolve));

        return framesHash;
    };

    private parseAtlas(resource: loaders.Resource, atlas: Atlas): void {
        const spritesheet = new PIXI.Spritesheet(
            resource.texture.baseTexture,
            atlas,
        );

        spritesheet.parse(function () {
            resource.spritesheet = spritesheet;
            resource.textures = spritesheet.textures;
        });
    }

    private handleResourceLoaded(resource: PIXI.loaders.Resource): void {
        const onResourceLoaded = this.onResourceLoaded;

        onResourceLoaded(resource.name as ResourceID, resource.texture || resource.data);

        // Set any and all texture-atlas textures as their own resource when the texture-atlas is loaded.
        if (resource.textures) {
            Object.entries(resource.textures).forEach(([atlasKey, value]) => {
                onResourceLoaded(atlasKey as AtlasKey, value);
            });
        }
    }
}
